import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { EliteLandingPlan } from '../planTypes'
import type { EliteOnboardingStatusResponse } from '../../../types/misc.types'
import type { StatusResponse } from '../../../types/rebalance'
import { smartRebalance } from '../../../repository/rebalance'
import { postCTA as postCta } from '../../../utils/appBridge'
import {
  resolvePlanId,
  resolveSelectedPlanForCta,
  updateCtaWithSelectedPlanId
} from './NewEliteLandingPlanSelector'
import { NewEliteLandingRebalancingTimerSection } from './NewEliteLandingRebalancingTimerSection'
import { NewEliteLandingRebalancingPlanSection } from './NewEliteLandingRebalancingPlanSection'
import type { FamilyOnboardingViewProps } from '../pages/family/familyOnboardingViewProps'

export type RebalanceWrapperSectionProps = {
  isStatusLoading: boolean
  cta?: EliteOnboardingStatusResponse['cta']
  eligiblePlans: EliteOnboardingStatusResponse['eligiblePlans']
  selectedPlan: EliteLandingPlan
  isCtaDisabled: boolean
  numberOfFundsToRebalance: number
  missedGains: number
  isForceUpdateRequired: boolean
  familyConfig: FamilyOnboardingViewProps
}

export function RebalanceWrapperSection({
  isStatusLoading,
  cta,
  eligiblePlans,
  selectedPlan,
  isCtaDisabled,
  numberOfFundsToRebalance,
  missedGains,
  isForceUpdateRequired,
  familyConfig
}: RebalanceWrapperSectionProps) {
  const { data: smartRebalanceResponse } = useQuery<{ data?: StatusResponse }, Error>({
    queryKey: ['SMART_REBALANCE'],
    queryFn: smartRebalance,
    retry: 1,
    refetchOnWindowFocus: false
  })

  const handleRebalancingSectionCtaClick = useCallback(() => {
    if (isCtaDisabled || !cta) return
    const eligiblePlanForCta = resolveSelectedPlanForCta(eligiblePlans, selectedPlan)
    const selectedPlanId = resolvePlanId(eligiblePlanForCta)
    if (!selectedPlanId) {
      postCta(JSON.stringify(cta))
      return
    }
    postCta(JSON.stringify(updateCtaWithSelectedPlanId(cta, selectedPlanId)))
  }, [cta, eligiblePlans, isCtaDisabled, selectedPlan])

  if (isStatusLoading || familyConfig?.isEliteBlockedBecausePrimaryNotElite) return null

  return (
    <div className="pb-6 pt-2 border-b-[12px] border-b-[#EBF2F2]" >
      <NewEliteLandingRebalancingTimerSection
        timeToRebalancePlanDrop={smartRebalanceResponse?.data?.timeToRebalancePlanDrop}
        selectedPlan={selectedPlan}
        familyConfig={familyConfig}
        isCtaDisabled={isCtaDisabled}
        isForceUpdateRequired={isForceUpdateRequired}
        onCtaClick={handleRebalancingSectionCtaClick}
      />
      <NewEliteLandingRebalancingPlanSection
        numberOfFundsToRebalance={numberOfFundsToRebalance}
        missedGains={missedGains}
        selectedPlan={selectedPlan}
        familyConfig={familyConfig}
        isCtaDisabled={isCtaDisabled}
        isForceUpdateRequired={isForceUpdateRequired}
        onCtaClick={handleRebalancingSectionCtaClick}
      />
    </div>
  )
}
